import { useEffect, useMemo, useState } from "react";
import { Tag, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";

const DISCOUNT_KIND_OPTIONS = [
  { value: "PERCENT", label: "Percent off" },
  { value: "FIXED", label: "Fixed amount off" },
  { value: "FIXED_PRICE", label: "Fixed price" },
] as const;

const DISCOUNT_SCOPE_OPTIONS = [
  { value: "MATCHED_ITEMS", label: "Matched items" },
  { value: "CART", label: "Entire cart" },
] as const;
import {
  Badge,
  Button,
  Input,
  SearchSelect,
  Toggle,
  statusColor,
  useConfirm,
} from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { useQueryRecordId } from "../lib/use-query-record";

type ActionKind = "PERCENT" | "FIXED" | "FIXED_PRICE";
type ActionScope = "MATCHED_ITEMS" | "CART";

interface DiscountConditions {
  productIds?: string[];
  categoryIds?: string[];
  productTypes?: string[];
  minQuantity?: number;
  minCartTotal?: number;
}

interface DiscountAction {
  kind: ActionKind;
  value: number;
  scope: ActionScope;
}

interface DiscountRow {
  id: string;
  name: string;
  active: boolean;
  priority: number;
  stackable: boolean;
  conditions: DiscountConditions;
  action: DiscountAction;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
}

interface DiscountForm {
  name: string;
  active: boolean;
  priority: string;
  stackable: boolean;
  kind: ActionKind;
  value: string;
  scope: ActionScope;
  minQuantity: string;
  minCartTotal: string;
  startsAt: string;
  endsAt: string;
}

function emptyForm(): DiscountForm {
  return {
    name: "",
    active: true,
    priority: "0",
    stackable: false,
    kind: "PERCENT",
    value: "",
    scope: "MATCHED_ITEMS",
    minQuantity: "",
    minCartTotal: "",
    startsAt: "",
    endsAt: "",
  };
}

function rowToForm(row: DiscountRow): DiscountForm {
  return {
    name: row.name,
    active: row.active,
    priority: String(row.priority),
    stackable: row.stackable,
    kind: row.action.kind,
    value: String(row.action.value),
    scope: row.action.scope,
    minQuantity: row.conditions.minQuantity != null ? String(row.conditions.minQuantity) : "",
    minCartTotal: row.conditions.minCartTotal != null ? String(row.conditions.minCartTotal) : "",
    startsAt: row.startsAt ? row.startsAt.slice(0, 16) : "",
    endsAt: row.endsAt ? row.endsAt.slice(0, 16) : "",
  };
}

function formToBody(form: DiscountForm) {
  const conditions: DiscountConditions = {};
  if (form.minQuantity.trim()) conditions.minQuantity = Number.parseInt(form.minQuantity, 10);
  if (form.minCartTotal.trim()) conditions.minCartTotal = Number.parseFloat(form.minCartTotal);

  return {
    name: form.name.trim(),
    active: form.active,
    priority: Number.parseInt(form.priority, 10) || 0,
    stackable: form.stackable,
    conditions,
    action: {
      kind: form.kind,
      value: Number.parseFloat(form.value) || 0,
      scope: form.scope,
    },
    startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
    endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
  };
}

function describeAction(row: DiscountRow): string {
  const { kind, value, scope } = row.action;
  const prefix =
    kind === "PERCENT" ? `${value}% off` : kind === "FIXED" ? `RM ${value} off` : `Fixed RM ${value}`;
  return `${prefix} (${scope === "CART" ? "cart" : "matched items"})`;
}

export function Discounts() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<DiscountRow | null>(null);
  const [form, setForm] = useState<DiscountForm>(emptyForm());
  const { recordId, clearRecordId } = useQueryRecordId();

  const canWrite = can("discounts.write");

  const listQuery = useQuery({
    queryKey: ["discounts"],
    queryFn: () => api<{ items: DiscountRow[] }>("/discounts"),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: ReturnType<typeof formToBody> }) => {
      if (payload.id) {
        return api(`/discounts/${payload.id}`, { method: "PUT", body: payload.body });
      }
      return api<{ id: string }>("/discounts", { method: "POST", body: payload.body });
    },
    onSuccess: () => {
      toast("success", editing ? "Discount updated" : "Discount created");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["discounts"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/discounts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["discounts"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Delete failed"),
  });

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  }

  function openEdit(row: DiscountRow) {
    setEditing(row);
    setForm(rowToForm(row));
    setEditorOpen(true);
  }

  function openDuplicate(row: DiscountRow) {
    setEditing(null);
    setForm({ ...rowToForm(row), name: `${row.name} (copy)` });
    setEditorOpen(true);
  }

  const tableRowActions = useMemo(
    () =>
      canWrite
        ? {
            onEdit: openEdit,
            onDuplicate: openDuplicate,
            onDelete: async (row: DiscountRow) => {
              await deleteMutation.mutateAsync(row.id);
            },
          }
        : undefined,
    [canWrite, deleteMutation],
  );

  const tableSelection = useMemo(
    () =>
      canWrite
        ? {
            entityLabel: "Discount",
            onEdit: (rows: DiscountRow[]) => openEdit(rows[0]!),
            onDuplicate: (rows: DiscountRow[]) => rows.forEach(openDuplicate),
            onDelete: async (rows: DiscountRow[]) => {
              await Promise.all(rows.map((r) => deleteMutation.mutateAsync(r.id)));
            },
          }
        : undefined,
    [canWrite, deleteMutation],
  );

  useBottomBarActions(() => {
    if (!canWrite) return [];
    if (editorOpen) {
      return [
        { id: "cancel", label: "Cancel", onClick: closeEditor },
        {
          id: "save",
          label: "Save",
          variant: "primary" as const,
          disabled: saveMutation.isPending || !form.name.trim(),
          onClick: () => saveMutation.mutate({ id: editing?.id, body: formToBody(form) }),
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  useEffect(() => {
    if (!recordId || !listQuery.data?.items) return;
    const row = listQuery.data.items.find((item) => item.id === recordId);
    if (!row) return;
    openEdit(row);
    clearRecordId();
  }, [recordId, listQuery.data?.items, clearRecordId]);

  const columns: Column<DiscountRow>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Rule",
        sortable: true,
        sortValue: (r) => r.name,
        exportValue: (r) => `${r.name} — ${describeAction(r)}`,
        render: (r) => (
          <div>
            <p className="font-medium">{r.name}</p>
            <p className="text-[10px] text-white/40">{describeAction(r)}</p>
          </div>
        ),
      },
      {
        key: "priority",
        header: "Priority",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.priority,
        render: (r) => <span className="text-xs">{r.priority}</span>,
      },
      {
        key: "active",
        header: "Status",
        sortable: true,
        sortValue: (r) => r.active,
        exportValue: (r) => (r.active ? "Active" : "Inactive"),
        render: (r) => (
          <Badge color={r.active ? statusColor("PAID") : statusColor("CANCELLED")}>
            {r.active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        key: "stackable",
        header: "Stack",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.stackable,
        exportValue: (r) => (r.stackable ? "Yes" : "No"),
        render: (r) => <span className="text-xs text-white/50">{r.stackable ? "Yes" : "No"}</span>,
      },
      {
        key: "schedule",
        header: "Schedule",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.startsAt ?? r.endsAt ?? "",
        exportValue: (r) =>
          r.startsAt || r.endsAt
            ? `${r.startsAt ? fmtDate(r.startsAt) : "—"} → ${r.endsAt ? fmtDate(r.endsAt) : "—"}`
            : "Always",
        render: (r) => (
          <span className="text-xs text-white/50">
            {r.startsAt || r.endsAt
              ? `${r.startsAt ? fmtDate(r.startsAt) : "—"} → ${r.endsAt ? fmtDate(r.endsAt) : "—"}`
              : "Always"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <ListPageShell>
      <DataTable
          columns={columns}
          rows={listQuery.data?.items}
          rowKey={(r) => r.id}
          loading={listQuery.isLoading}
          exportFilename="discounts"
          onRowClick={canWrite ? openEdit : undefined}
          entityLabel="Discount"
          rowActions={tableRowActions}
          selection={tableSelection}
          empty={{
            icon: Tag,
            title: "No discount rules yet",
            action: canWrite ? (
              <Button size="sm" onClick={openCreate}>
                Create
              </Button>
            ) : undefined,
          }}
        />

      <RightSidebarPanel
        open={editorOpen}
        title={editing ? "Edit discount" : "Create discount"}
        subtitle={editing?.name}
        wide
        onClose={closeEditor}
        footer={
          canWrite && (
            <div className="flex flex-wrap justify-end gap-2">
              {editing && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deleteMutation.isPending}
                  onClick={() =>
                    confirm({
                      title: "Delete discount?",
                      message: `Remove ${editing.name}?`,
                      confirmLabel: "Delete",
                      danger: true,
                      action: async () => {
                        await deleteMutation.mutateAsync(editing.id);
                        toast("success", "Discount deleted");
                      },
                    })
                  }
                >
                  Delete
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={saveMutation.isPending || form.name.trim().length < 2 || !form.value.trim()}
                onClick={() => saveMutation.mutate({ id: editing?.id, body: formToBody(form) })}
              >
                Save
              </Button>
            </div>
          )
        }
      >
        <div className="sidebar-form">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <div className="form-row-2">
            <Input
              label="Priority"
              type="number"
              hint="Higher runs first"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
            />
            <SearchSelect
              label="Discount type"
              options={[...DISCOUNT_KIND_OPTIONS]}
              value={form.kind}
              onChange={(v) => setForm((f) => ({ ...f, kind: v as ActionKind }))}
            />
          </div>
          <div className="form-row-2">
            <Input
              label="Value"
              type="number"
              min="0"
              step="0.01"
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
            />
            <SearchSelect
              label="Applies to"
              options={[...DISCOUNT_SCOPE_OPTIONS]}
              value={form.scope}
              onChange={(v) => setForm((f) => ({ ...f, scope: v as ActionScope }))}
            />
          </div>

          <div className="rounded-xl border border-line bg-white/[0.02] p-3">
            <p className="mb-3 font-display text-xs font-semibold uppercase tracking-wider text-white/40">
              Conditions
            </p>
            <div className="space-y-3">
              <Input
                label="Minimum quantity"
                type="number"
                min="1"
                hint="Optional"
                value={form.minQuantity}
                onChange={(e) => setForm((f) => ({ ...f, minQuantity: e.target.value }))}
              />
              <Input
                label="Minimum cart total (MYR)"
                type="number"
                min="0"
                step="0.01"
                hint="Optional"
                value={form.minCartTotal}
                onChange={(e) => setForm((f) => ({ ...f, minCartTotal: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-row-2">
            <Input
              label="Starts at"
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
            />
            <Input
              label="Ends at"
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
            />
          </div>

          <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label="Active" />
          <Toggle
            checked={form.stackable}
            onChange={(v) => setForm((f) => ({ ...f, stackable: v }))}
            label="Stackable with other rules"
          />
        </div>
      </RightSidebarPanel>

      {dialog}
    </ListPageShell>
  );
}
